import {
  Backdrop,
  LoadingSpinnerIconTwoTone,
  Typography,
  UpcomingAuditionIcon,
} from '@room-match/shared-ui-components';
import { Avatar } from 'components/Avatar';
import React, { useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ROUTES } from 'shared/constants/ROUTES';
import { AuditionPhase } from 'shared/enums/AuditionPhase';
import { AuditionStatus } from 'shared/enums/AuditionStatus';
import { ICompanyAddressAttributes } from 'shared/interfaces/ICompany';
import { readableProjectUnion } from 'shared/utils/room-shared';

import { CustomTypography } from 'shared/elements/CustomTypography';
import { auditionScheduleService } from 'shared/services/auditionScheduleService';
import { getClockColor } from 'shared/utils/scheduleTimeCompute';
import { STTimeDateParser, auditionTimeParse, timeConvert, timeParser } from 'shared/utils/timeHelpers';
import { CardContainer, CardHeader, EmptyContainer } from '../Dashboard.styles';
import { ItemContainer, Left, ListContainer, Middle, Right } from './UpcomingAuditions.styles';

type Props = {
  count: number;
};

const { getAuditionSchedules } = auditionScheduleService();

const UpcomingAuditions: React.FC<Props> = ({ count = 0 }) => {
  const history = useHistory();
  const { data, isLoading } = getAuditionSchedules({
    phase: AuditionPhase.SUBMISSION,
    status: AuditionStatus.CONFIRMED,
    first: 2,
    after: '',
  });

  const getData = useMemo(() => {
    if (isLoading) {
      return;
    } else {
      return data
        ? data.pages.filter((v, index) => index === 0).map((i) => i.data.talent_audition_schedules_connection.nodes)
        : undefined;
    }
  }, [data, isLoading]);

  if (isLoading) return <Backdrop isLoading={true} />;
  return (
    <CardContainer>
      <CardHeader>
        <div>
          <UpcomingAuditionIcon />
          <span style={{ fontSize: 18 }}>Upcoming Auditions</span>
        </div>
        <Link to={`${ROUTES.TALENT.AUDITIONS}/upcoming_auditions`}>
          <Typography fontSize={14} style={{ color: '#039e63' }}>
            {count === 0 ? `` : count > 1 ? `${count} Auditions` : `${count} Audition`}
          </Typography>
        </Link>
      </CardHeader>
      <ListContainer>
        {isLoading ? (
          <LoadingSpinnerIconTwoTone />
        ) : getData && getData[0].length > 0 ? (
          getData[0].map(
            (i) =>
              i && (
                <ItemContainer onClick={() => history.push(`${ROUTES.TALENT.AUDITIONS}/upcoming_auditions/${i.id}`)}>
                  <Left>
                    {i.audition.casting_role.project.logo ? (
                      <img
                        src={`${i.audition.casting_role.project.logo}/40x40`}
                        style={{ width: 44, height: 44, borderRadius: 5 }}
                      />
                    ) : (
                      <Avatar
                        style={{ height: 44, width: 44, borderRadius: 5 }}
                        title={i.audition.casting_role.project.name}
                        uuid={i.audition.casting_role.project.id}
                      />
                    )}
                    <div>
                      <CustomTypography fontSize={12}>
                        {i.audition.casting_role.project.company.addresses
                          .filter((v: ICompanyAddressAttributes) => v.primary === true)
                          .map((i: ICompanyAddressAttributes) => `${i.location_name}, ${i.city_suburb}`) || '–'}
                      </CustomTypography>
                      <CustomTypography fontWeight={500}>
                        {i.audition.casting_role.project.name || '–'}
                      </CustomTypography>
                      <CustomTypography fontSize={12}>
                        {i.audition.casting_role.project.union
                          ? readableProjectUnion(i.audition.casting_role.project.union)
                          : '–'}
                      </CustomTypography>
                    </div>
                  </Left>
                  <Middle>
                    <div>
                      <CustomTypography fontSize={14}>Rep:</CustomTypography>
                      <CustomTypography fontSize={14} color="primary">
                        {i.audition.profile.agency?.full_name || i.audition.profile.agency?.name ? (
                          <CustomTypography color="primary" fontSize={14}>
                            {i.audition.profile.agency?.full_name || i.audition.profile.agency?.name}
                          </CustomTypography>
                        ) : (
                          <CustomTypography color="primary" fontSize={14}>
                            Non Represented
                          </CustomTypography>
                        )}
                      </CustomTypography>
                    </div>
                    <div>
                      <CustomTypography fontSize={14}>Role:</CustomTypography>
                      <CustomTypography
                        fontSize={14}
                        style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: 150 }}
                      >
                        {i.audition.casting_role.name || '–'}
                      </CustomTypography>
                    </div>
                    <div>
                      <CustomTypography fontSize={14}>Location:</CustomTypography>
                      <CustomTypography fontSize={14}>
                        {i.audition.profile.agency?.state && i.audition.profile.agency?.country
                          ? `${i.audition.profile.agency?.state}, ${i.audition.profile.agency?.country}`
                          : i.audition.profile.agency?.state
                            ? i.audition.profile.agency?.state
                            : i.audition.profile.agency?.country
                              ? i.audition.profile.agency?.country
                              : ''}
                      </CustomTypography>
                    </div>
                    <div>
                      <CustomTypography fontSize={14}>Date & Time:</CustomTypography>
                      <CustomTypography fontSize={14}>
                        {i.phase === 'Self Tapes Request'
                          ? STTimeDateParser(
                              i.audition.casting_role.self_tapes_requirements?.date || '',
                              i.audition.casting_role.self_tapes_requirements?.time || '',
                              i.audition.casting_role.self_tapes_requirements?.timezone || '',
                            )
                          : timeConvert(i)}
                      </CustomTypography>
                    </div>
                  </Middle>
                  <Right>
                    <span style={{ fontSize: 14, color: getClockColor(i) }}>
                      {i.phase === 'Self Tapes Request'
                        ? timeParser(
                            i.audition.casting_role.self_tapes_requirements?.date || '',
                            i.audition.casting_role.self_tapes_requirements?.time || '',
                            i.audition.casting_role.self_tapes_requirements?.timezone || '',
                          )
                        : auditionTimeParse(i)}
                    </span>
                  </Right>
                </ItemContainer>
              ),
          )
        ) : (
          <EmptyContainer>No data available</EmptyContainer>
        )}
      </ListContainer>
    </CardContainer>
  );
};

export default UpcomingAuditions;
